import {
  Alert,
  Box,
  Button,
  Card,
  Heading,
  Skeleton,
  Text,
} from "@chakra-ui/react";
import { formatCop, formatRate, rateFor, vatLabel } from "../format";
import type { BenchInput } from "../types";
import { useInductionQuote } from "../useInductionQuote";

type Props = {
  bench: BenchInput;
  withVat: boolean;
  onBack: () => void;
  onNext: () => void;
};

const SavingStep = ({ bench, withVat, onBack, onNext }: Props): JSX.Element => {
  const { quote, isError, refetch } = useInductionQuote({ bench });
  // Moving every pre-approved contract: the last row.
  const all = quote?.rows[quote.rows.length - 1];

  return (
    <Box display="flex" flexDirection="column" gap="1.5rem">
      <Card.Root borderRadius="lg" border="none">
        <Card.Body p="5" display="flex" flexDirection="column" gap="4">
          {quote && all ? (
            <>
              {all.monthlySaving > 0 ? (
                <Box>
                  <Text fontWeight="semibold" color="green.700">
                    Tu ahorro potencial
                  </Text>
                  <Heading size="4xl" color="green.700">
                    hasta {formatCop(all.monthlySaving * 12)} al año
                  </Heading>
                  <Text fontWeight="semibold">
                    Es un {Math.round(all.savingPct * 100)}% menos de lo que
                    pagas hoy.
                  </Text>
                </Box>
              ) : (
                <Box>
                  <Heading size="xl">
                    Con tu tarifa actual no vemos un ahorro
                  </Heading>
                  <Text color="gray.600">
                    Aun así puedes continuar y revisar tus pre-aprobados.
                  </Text>
                </Box>
              )}
              <Box
                display="grid"
                gridTemplateColumns={{ base: "1fr", sm: "1fr 1fr" }}
                gap="3"
              >
                <Box bg="gray.50" borderRadius="md" p="4">
                  <Text fontWeight="semibold" color="gray.600">
                    Pagas hoy
                  </Text>
                  <Heading size="2xl">
                    {formatRate(
                      withVat
                        ? quote.currentRateWithVat
                        : quote.currentRateWithoutVat
                    )}{" "}
                    <Text
                      as="span"
                      fontSize="sm"
                      fontWeight="normal"
                      color="gray.500"
                    >
                      {vatLabel(withVat)}
                    </Text>
                  </Heading>
                </Box>
                <Box bg="gray.50" borderRadius="md" p="4">
                  <Text fontWeight="semibold" color="gray.600">
                    Tasa Palomma desde
                  </Text>
                  <Heading size="2xl">
                    {formatRate(rateFor(all, withVat))}{" "}
                    <Text
                      as="span"
                      fontSize="sm"
                      fontWeight="normal"
                      color="gray.500"
                    >
                      {vatLabel(withVat)}
                    </Text>
                  </Heading>
                </Box>
              </Box>
            </>
          ) : isError ? (
            <Alert.Root status="error" colorPalette="red">
              <Alert.Indicator />
              <Alert.Content>
                <Alert.Description>
                  No pudimos calcular tu ahorro.
                </Alert.Description>
              </Alert.Content>
              <Button size="sm" variant="outline" onClick={() => refetch()}>
                Reintentar
              </Button>
            </Alert.Root>
          ) : (
            <Skeleton height="120px" />
          )}
        </Card.Body>
      </Card.Root>
      <Alert.Root status="warning" colorPalette="orange">
        <Alert.Indicator />
        <Alert.Content>
          <Alert.Description>
            La tasa y el ahorro dependen de validar la información que
            declaraste.
          </Alert.Description>
        </Alert.Content>
      </Alert.Root>
      <Card.Root borderRadius="lg" border="none">
        <Card.Body p="5" display="flex" flexDirection="column" gap="4">
          <Box>
            <Heading size="lg">Somos aliados</Heading>
            <Text color="gray.600">
              Por eso te mostramos el número antes de pedirte nada más. Mientras
              más contratos afianzas con nosotros, más baja es la tasa. En el
              siguiente paso eliges cuáles.
            </Text>
          </Box>
          <Box display="flex" justifyContent="space-between" gap="3">
            <Button variant="outline" onClick={onBack}>
              Atrás
            </Button>
            <Button disabled={!quote} onClick={onNext}>
              Ver mis pre-aprobados
            </Button>
          </Box>
        </Card.Body>
      </Card.Root>
    </Box>
  );
};

export default SavingStep;
